import { isString } from 'mathjs';
import React from 'react';
import Levenshtein from 'fast-levenshtein';
import ColorUtils from './ColorUtils';
import { CssUnits } from '../../interfaces/VisageUI';
import { Gradient, GradientColorStopTypes, GradientOrientations, GradientTypes } from '../../interfaces/GradientColor';
import { SupportedRichTextTags } from '../../interfaces/Misc';

const gradientParser = require('gradient-parser');

export default class TextUtils
{
	/**
	 * - based on https://stackoverflow.com/a/31415820/618726
	 * @static
	 * @param {String} input
	 * @returns true if the given input string is all lowercase, or false otherwise
	 * @memberof TextUtils
	*/
	static isLowerCase(input: string): boolean
	{
		return (input !== null && input !== undefined) && input === input.toLowerCase() && input !== input.toUpperCase();
	}

	/**
	 * used to make sure texts are human friendly and readable for presentation in the UI,
	 * epecially when the source input comes from code or auto-generated strings as as camelCasing
	 *
	 * the rules for text transformation are as follows:
		1. replace underscore with space such that test_text -> test text
		2. between words such that testText -> test Text, and TestText -> TestText
		3. between the last latter in a word and a following number such that test1 -> test 1, and TEXT3 -> TEXT 3
		4. when a lowercase letter follows a number, such that 123test -> 123 test, and test123text -> test 123 text
		5. when an uppercase letter is followed by a lowercase letter but follows an uppercase letter, such that TESTText -> TEST Text
		6. when an uppercase letter follows another uppercase letter that follows a number, such that 3DGUI -> 3D GUI
	 * 
	 *	if capitalization is set to true, the first letter of each word in the output will be capitalized
	 * @static
	 * @param {String} input the source text we wish to transform into a more human-friendly
	 * @returns the formatted text, or an empty string if the input was invalid
	 * @memberof TextUtils
	 */
	static prettifyText(input: string, options?: { capitalize?: boolean, dehyphenize?: boolean }) : string
	{
		if (!input || !isString(input)) return "";

		const defaultOptions = options ?? {};
		const { 
			capitalize = false, 
			dehyphenize = false 
		} = defaultOptions;

		const text = input.trim();
		if(!text) return "";

		const output = new Array<string>();

		function isCharNumber(char: string | number) {return char >= '0' && char <= '9';}
		function isLowerCase(char: string) {return char === char.toLowerCase() && !isCharNumber(char);}
		function isUpperCase(char: string) {return char === char.toUpperCase() && !isCharNumber(char);}
		function isLetter(char) { return char.toLowerCase() != char.toUpperCase();}

		enum charTypes {
			unknown,
			lowerCase,
			upperCase,
			number,
			space,
		}

		function getCharType(char: string)
		{
			if(char === ' ') return charTypes.space;
			if(isCharNumber(char)) return charTypes.number;
			if(isLowerCase(char)) return charTypes.lowerCase;
			if(isUpperCase(char)) return charTypes.upperCase;
			return charTypes.unknown;
		}

		let currentCharType = charTypes.unknown;
		let prevCharType = charTypes.unknown;

		for(let i = 0; i < text.length; i++)
		{
			let char = text.charAt(i);
			if(char === '_') char = ' '; // replace underscore with space ('test_text' -> 'test text')
			if(dehyphenize && char === '-') char = ' '; // replace hyphen with space ('test-text' -> 'test text'

			currentCharType = getCharType(char);

			// insert space where needed according to the rules
			if(i > 0)
			{
				// when an uppercase follows a lowercase ('testText' -> 'test Text')
				if(currentCharType === charTypes.upperCase && prevCharType === charTypes.lowerCase)
				{
					output.push(' ');
				}
				// from any letter caseing to a number ('test1' -> 'test 1', 'TEST3' -> 'TEST 3')
				else if(currentCharType === charTypes.number && (prevCharType === charTypes.lowerCase || prevCharType === charTypes.upperCase))
				{
					output.push(' ');
				}
				// from a number to lowercase letter ('123test' -> '123 test', 'test123text' -> 'test 123 text')
				else if(currentCharType === charTypes.lowerCase && prevCharType === charTypes.number)
				{
					output.push(' ');
				}
				// an uppercase letter that follows an uppercase letter or a number but is followed by lowercase letter ('TESTText' -> 'TEST Text', '123Test' -> '123 Test')
				else if(currentCharType === charTypes.upperCase && (prevCharType === charTypes.upperCase || prevCharType === charTypes.number))
				{
					const nextChar = text.charAt(i + 1);
					if(nextChar && getCharType(nextChar) === charTypes.lowerCase && isLetter(nextChar))
					{
						output.push(' ');
					}
				}
			}

			// skip inserting the char if it's a space that follows a space ('test  text' -> 'test text')
			if(currentCharType === charTypes.space && prevCharType === charTypes.space) continue;

			// insert the char into the output and update the previous char type
			output.push(char);
			prevCharType = currentCharType;
		}

		// capitalize first letter
		output[0] = output[0].toUpperCase();

		// capitalize each letter that is preceded by a space
		if(capitalize)
		{
			for (let i = 0; i < output.length; i++)
			{
				const char = output[i];
				const prevChar = output[i - 1];

				if (prevChar === ' ')
				{
					output[i] = char.toUpperCase();
				}
			}
		}

		return output.join('');
	}

	/**
	 * searches for 'serachTerm' within the given 'string'
	 * it first attempts a regex search, and then falls back to regular string.includes method
	 * @static
	 * @param {String} string the source string to search within
	 * @param {String} searchTerm either a regex expression (must be a complete and valid expression) or a substring, used to determine matches
	 * @returns true if any matches of 'serachTerm' are found within 'string'
	 * @memberof TextUtils
	 */
	static searchString(string: string, searchTerm: string | RegExp)
	{
		try
		{
			return string.match(new RegExp(searchTerm, 'gi'));
		}
		catch
		{
			if (!string || !searchTerm) return false;
			return string.toLowerCase().includes(String(searchTerm).toLowerCase());
		}
	}

	/**
	 * coonverts plain text with line breaks into list of lines separated by html <br/> elements
	 *
	 * @static
	 * @param {*} input
	 * @returns
	 * @memberof TextUtils
	 */
	static convertNewlineToHtmlBreaks(input)
	{
		const output = new Array<JSX.Element>();
		const lines = input.split('\n');
		for(const line of lines)
		{
			output.push(line, <br/>);
		}

		return output;
	}

	static wrapContextText(ctx, font, text, maxWidth) 
	{
		//https://fjolt.com/article/html-canvas-how-to-wrap-text modified to also support single words that are longer than the max width

		ctx.font = font;
		// First, start by splitting all of our text into words, but splitting it into an array split by spaces
		const words = text.split(' ');
		let line = ''; // This will store the text of the current line
		let testLine = ''; // This will store the text when we add a word, to test if it's too long
		const lineArray: any[] = []; // This is an array of lines, which the function will return
	
		// Lets iterate over each word
		for(let n = 0; n < words.length; n++) 
		{
			//if a singular word is longer than the max width, we split it up in the middle
			const testWord = `${words[n]}`;
			const wordWidth = ctx.measureText(testWord).width;
			if (wordWidth > maxWidth && testWord.length > 1)
			{
				//push the current line into the array
				if(line.length) lineArray.push(line);

				//start removing characters from the end of the word until it fits
				let fittingSubstr = testWord.substring(0, testWord.length -1);
				let fittingSubstrWidth = ctx.measureText(fittingSubstr).width;

				while(fittingSubstrWidth > maxWidth && fittingSubstr.length > 1)
				{
					fittingSubstr = fittingSubstr.substring(0, fittingSubstr.length - 1);
					fittingSubstrWidth = ctx.measureText(fittingSubstr).width;
				}
				
				line = `${fittingSubstr}`;
				lineArray.push(line);
				line = ``;
				testLine = ``;
				const remainder = testWord.substring(fittingSubstr.length);
				words.splice(n+1, 0, remainder);
				continue;
			}

			// Create a test line, and measure it..
			testLine += `${words[n]} `;
			const lineWidth = ctx.measureText(testLine).width;
			// If the width of this test line is more than the max width
			if (lineWidth > maxWidth && n > 0) 
			{
				// Then the line is finished, push the current line into "lineArray"
				if(line.length) lineArray.push(line);

				// Update line and test line to use this word as the first word on the next line
				line = `${words[n]} `;
				testLine = `${words[n]} `;
			}
			else 
			{
				// If the test line is still less than the max width, then add the word to the current line
				line += `${words[n]} `;
			}
			// If we never reach the full max width, then there is only one line.. so push it into the lineArray so we return something
			if(n === words.length - 1) 
			{
				lineArray.push(line.substring(0, line.length - 1));
			}
		}
		// Return the line array
		return lineArray;
	}
	

	/**
	 * removes all whitespace from the given string
	 *
	 * @static
	 * @param {string} input
	 * @returns {string}
	 * @memberof TextUtils
	 */
	static removeWhiteSpace(input: string): string
	{
		return String(input).replace(/\s/g, '');
	}

	/**
	 * checks if the given input is a valid URL
	 *
	 * @static
	 * @param {string} input
	 * @returns {boolean}
	 * @memberof TextUtils
	 */
	static isValidURL(input: string): boolean 
	{
		// thanks to https://www.freecodecamp.org/news/how-to-validate-urls-in-javascript
		const pattern = new RegExp(
			'^([a-zA-Z]+:\\/\\/)' + // protocol (made mandatory)
			'((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
			'((\\d{1,3}\\.){3}\\d{1,3})|' + // OR IP (v4) address
			'localhost)' +  // OR localhost
			'(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
			'(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
			'(\\#[-a-z\\d_]*)?$', // fragment locator
			'i'
		);
		return pattern.test(input);
	}
	
	/**
	 * parses a css unit string into a value and a unit, for example "10px" -> { value: "10", unit: "px" }
	 * if the unit is not allowed, the unit will be an empty string
	 *
	 * @static
	 * @param {string} [input]
	 * @param {CssUnits[]} [allowedUnits=[CssNumericUnits.px, CssNumericUnits.vh, CssNumericUnits.vw, CssNumericUnits.percent]]
	 * @returns {{ value: string, unit: string }}
	 * @memberof TextUtils
	 */
	static parseCssUnitString(input?: string, allowedUnits: CssUnits[] = [CssUnits.px, CssUnits.vh, CssUnits.vw, CssUnits.percent]): { value: string, unit: string } 
	{
		if (!input) return { value: "", unit: "" };

		// Regular expression to match the format [number][non-number]
		const regex = /^(-?\d+(\.\d+)?)([^0-9]+)$/;

		// Test the input against the regular expression
		const match = input.trim().match(regex);

		if (match) 
		{
			// If it matches, extract the number and unit
			const value = match[1]; // The numeric part
			const unit = match[3]; // The non-numeric part

			// Check if the unit is one of the allowed units, if provided
			if (!allowedUnits || allowedUnits.includes(unit as CssUnits))
			{
				return { value, unit };
			}
		}

		// If the input doesn't match the pattern or the unit is not allowed
		return { value: input, unit: "" };
	}

	/**
	 * ensures the input string conforms to the desired graeidnt format that we can feed to the gradient picker component
	 * 1) gradient must include a deg definition, e.g. 90deg or 53deg
	 * 2) all colors must have a gradient stop defined in %, e.g. rgba(0, 0, 0, 1) 0%, rgba(255, 255, 255, 1) 45%
	 * 3) all colors must be in the rgba format, e.g. rgba(0, 0, 0, 1), rgba(128, 25, 53, 0.56)
	 *
	 * if the input is completely invalid, the default gradient is returned: "linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(255, 255, 255, 1) 100%)"
	 * 
	 * @static
	 * @param {string} input e.g. "linear-gradient(#333333, #ababab)"
	 * @returns {string} e.g. "linear-gradient(90deg, rgba(51, 51, 51, 1) 0%, rgba(171, 171, 171, 1) 100%)"
	 * @memberof TextUtils
	 */
	static fixCssGradientString(input: string): string
	{
		function convertColor(color: string): string
		{
			const hexAlpha = ColorUtils.cssColorToHexAlpha(color);
			const rgb = ColorUtils.hexToRgb(hexAlpha.color);
			return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${hexAlpha.alpha})`;
		}

		function getGradientOrientationString(gradientObject: Gradient): string
		{
			if(!gradientObject.orientation)
			{
				switch(gradientObject.type)
				{
					case GradientTypes.Linear:
					case GradientTypes.LinearRepeating:
						return "0deg";
					case "radial-gradient":
					case "repeating-radial-gradient":
						return "circle";
				}
			}
			else
			{
				switch(gradientObject.type)
				{
					case GradientTypes.Linear:
					case GradientTypes.LinearRepeating:
						if(gradientObject.orientation?.type === GradientOrientations.Directional)
						{
							// TODO: for now return default value, but we need to add more robust conversion from literal directions to angles, e.g. "to left" -> 180deg
							return `0deg`;
						}
						else if(gradientObject.orientation?.type === GradientOrientations.Angular)
						{
							return `${gradientObject.orientation.value}deg`;
						}
						break;
					case GradientTypes.Radial:
					case GradientTypes.RadialRepeating:
						// currently we only allow 1 shape, and it's a circle
						return "circle";
				}
			}

			return "";
		}

		try
		{
			const gradientObject: Gradient = gradientParser.parse(input)[0];
			let output = `${gradientObject.type}(${getGradientOrientationString(gradientObject)}, `;

			for(let i = 0; i < gradientObject.colorStops.length; i++)
			{
				const colorStop = gradientObject.colorStops[i];
				switch(colorStop.type)
				{
					case GradientColorStopTypes.Rgba:
						output += `rgba(${(colorStop.value as Array<string>).join(', ')})`;
						break;
					case GradientColorStopTypes.Rgb:
						const colorString = `rgb(${(colorStop.value as Array<string>).join(', ')})`;
						output += convertColor(colorString);
						break;
					case GradientColorStopTypes.Hex:
						const rgb = ColorUtils.hexToRgb(colorStop.value as string);
						output += `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`;
						break;
					case GradientColorStopTypes.Literal:
						output += convertColor(colorStop.value as string);
						break;
				}

				// ensure we have the gradient stop defined
				if(colorStop.length)
				{
					output += ` ${colorStop.length.value}${colorStop.length.type}`;
				}
				else
				{
					output += ` ${i / (gradientObject.colorStops.length - 1) * 100}%`;
				}

				// add a comma if we're not at the last color stop
				if(i < gradientObject.colorStops.length - 1)
				{
					output += ', ';
				}
			}

			output += ')';
			return output;
		}
		catch(error)
		{
			console.error("error parsing gradient input:\n" + input, error);
		}

		return "linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(255, 255, 255, 1) 100%)";
	}

	/**
	 * Ensures the input string is a valid filename by removing illegal characters and truncating it if necessary.
	 * Should comply with the rules for filenames across macOS, Linux, and Windows.
	 * 
	 * @static
	 * @param {string} input
	 * @return {*}  {string}
	 * @memberof TextUtils
	 */
	static sanitizeFilename(input: string): string 
	{
		// Define a regex pattern for visible characters that are illegal in filenames
		// across macOS, Linux, and Windows
		const illegalChars = /[<>:"/\\|?*]/g;

		// Replace illegal characters with underscores
		let sanitized = input.replace(illegalChars, '_');

		// Remove control characters manually
		sanitized = sanitized.split('').filter(char => 
		{
			const charCode = char.charCodeAt(0);
			return charCode > 31 && charCode !== 127;
		}).join('');

		// Remove leading and trailing spaces and dots
		sanitized = sanitized.trim().replace(/^\.+|\.+$/g, '');

		// Ensure the filename is not empty after sanitization
		if (sanitized.trim().length === 0 || /^[\_]+$/.test(sanitized))
		{
			sanitized = '_';
		}

		// Truncate the filename if it's too long (max 255 characters)
		sanitized = sanitized.slice(0, 255);

		return sanitized;
	}
	
	/**
	 * Encodes a string to base64, replacing '+' and '/' with '-' and '_', and removing padding.
	 *
	 * @static
	 * @param {string} input
	 * @return {*} string 
	 * @memberof TextUtils
	 */
	static encodeToBase64UrlSafe(input: string): string
	{
		if(!input) return "";

		const encoder = new TextEncoder();
		const data = encoder.encode(input);
		let binary = '';
		
		for (const byte of data) 
		{
			binary += String.fromCharCode(byte);
		}

		const base64 = window.btoa(binary);
		const base64UrlSafe = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

		return base64UrlSafe;
	}

	/**
	 * Decodes a base64 URL-safe string into a regular string.
	 * '-' and '_' are replaced with '+' and '/' respectively, and padding is added if needed before decoding
	 *
	 * @static
	 * @param {string} input
	 * @return {*}  {string}
	 * @memberof TextUtils
	 */
	static decodeFromBase64UrlSafe(input: string): string
	{
		if(!input) return "";

		// check if the input string is a safe base64 string and return the input as-is if not
		const validBase64 = /^[A-Za-z0-9-_]+={0,2}$/.test(input);
		if (!validBase64) 
		{
			console.warn('Invalid base64 string:', input);
			return input;
		}

		const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
		const padding = '='.repeat((4 - (base64.length % 4)) % 4); // Add padding if needed
		const base64Padded = base64 + padding;
		const binary = window.atob(base64Padded);
		const bytes = new Uint8Array(binary.length);
		
		for (let i = 0; i < binary.length; i++) 
		{
			bytes[i] = binary.charCodeAt(i);
		}

		const decoder = new TextDecoder();
		return decoder.decode(bytes);
	}

	/**
	 * Checks if the given text has rich text tags that are supported by Text Mesh Pro for the Unity app player
	 *
	 * @static
	 * @param {string} text
	 * @return {*}  {boolean}
	 * @memberof TextUtils
	 */
	static isRichTextTMP(text: string): boolean
	{
		if(!text) return false;

		for(const tag of SupportedRichTextTags)
		{
			if(text.includes(`<${tag}>`)) return true;
		}

		return false;
	}

	static fuzzyMatch(text: string, searchTerm: string, threshold = 0.8): boolean
	{
		const distance = Levenshtein.get(text, searchTerm, { useCollator: true });
		const maxLen = Math.max(text.length, searchTerm.length);
		const similarity = maxLen === 0 ? 1 : 1 - distance / maxLen;
		return similarity >= threshold;
	}
}